import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Building2,
  CalendarDays,
  CheckSquare,
  FileText,
  Image,
  LayoutDashboard,
  Lightbulb,
  Megaphone,
  Plug,
  Settings,
  Share2,
  Target,
  Users,
} from 'lucide-react';
import { Permission } from '@marketing-os/shared';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Basta ter UMA das permissões para ver o item. */
  permissions: Permission[];
  /** Marca o item como ativo também em rotas filhas. */
  matchPrefix?: string;
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

export const AGENCY_NAV: NavGroup[] = [
  {
    id: 'operacao',
    label: 'Operação',
    items: [
      {
        to: '/',
        label: 'Painel da agência',
        icon: LayoutDashboard,
        permissions: [Permission.METRIC_READ, Permission.CLIENT_READ],
      },
      {
        to: '/clientes',
        label: 'Clientes',
        icon: Building2,
        permissions: [Permission.CLIENT_READ],
        matchPrefix: '/clientes',
      },
      {
        to: '/aprovacoes',
        label: 'Aprovações',
        icon: CheckSquare,
        permissions: [Permission.APPROVAL_READ],
        matchPrefix: '/aprovacoes',
      },
    ],
  },
  {
    id: 'midia',
    label: 'Mídia paga',
    items: [
      {
        to: '/campanhas',
        label: 'Campanhas',
        icon: Megaphone,
        permissions: [Permission.CAMPAIGN_READ],
        matchPrefix: '/campanhas',
      },
      {
        to: '/criativos',
        label: 'Criativos',
        icon: Image,
        permissions: [Permission.CREATIVE_READ],
        matchPrefix: '/criativos',
      },
      {
        to: '/otimizacao',
        label: 'Otimização',
        icon: Lightbulb,
        permissions: [Permission.RECOMMENDATION_READ],
        matchPrefix: '/otimizacao',
      },
    ],
  },
  {
    id: 'conteudo',
    label: 'Conteúdo',
    items: [
      {
        to: '/social',
        label: 'Social orgânico',
        icon: Share2,
        permissions: [Permission.SOCIAL_READ],
        matchPrefix: '/social',
      },
      {
        to: '/conteudo',
        label: 'Blog e pautas',
        icon: FileText,
        permissions: [Permission.CONTENT_READ],
        matchPrefix: '/conteudo',
      },
    ],
  },
  {
    id: 'resultado',
    label: 'Resultado',
    items: [
      {
        to: '/leads',
        label: 'Leads e funil',
        icon: Target,
        permissions: [Permission.CONVERSION_READ],
        matchPrefix: '/leads',
      },
      {
        to: '/relatorios',
        label: 'Relatórios',
        icon: BarChart3,
        permissions: [Permission.REPORT_READ],
        matchPrefix: '/relatorios',
      },
    ],
  },
  {
    id: 'administracao',
    label: 'Administração',
    items: [
      {
        to: '/integracoes',
        label: 'Integrações',
        icon: Plug,
        permissions: [Permission.INTEGRATION_READ],
        matchPrefix: '/integracoes',
      },
      {
        to: '/usuarios',
        label: 'Usuários',
        icon: Users,
        permissions: [Permission.USER_READ],
        matchPrefix: '/usuarios',
      },
      {
        to: '/configuracoes',
        label: 'Configurações',
        icon: Settings,
        permissions: [
          Permission.ORG_MANAGE,
          Permission.PROMPT_MANAGE,
          Permission.AI_COST_READ,
          Permission.AUDIT_READ,
        ],
        matchPrefix: '/configuracoes',
      },
    ],
  },
];

/** Navegação reduzida do portal do cliente. */
export const PORTAL_NAV: NavItem[] = [
  {
    to: '/portal',
    label: 'Painel',
    icon: LayoutDashboard,
    permissions: [Permission.METRIC_READ],
  },
  {
    to: '/portal/aprovacoes',
    label: 'Aprovações',
    icon: CheckSquare,
    permissions: [Permission.APPROVAL_READ],
    matchPrefix: '/portal/aprovacoes',
  },
  {
    to: '/portal/relatorios',
    label: 'Relatórios',
    icon: BarChart3,
    permissions: [Permission.REPORT_READ],
    matchPrefix: '/portal/relatorios',
  },
  {
    to: '/portal/calendario',
    label: 'Calendário',
    icon: CalendarDays,
    permissions: [Permission.SOCIAL_READ, Permission.CONTENT_READ],
    matchPrefix: '/portal/calendario',
  },
];

/** Rótulos usados no breadcrumb a partir do segmento da URL. */
export const SEGMENT_LABELS: Record<string, string> = {
  '': 'Painel',
  portal: 'Portal do cliente',
  clientes: 'Clientes',
  novo: 'Novo',
  campanhas: 'Campanhas',
  criar: 'Criar',
  criativos: 'Criativos',
  gerador: 'Gerador',
  templates: 'Templates',
  social: 'Social orgânico',
  composer: 'Publicação',
  conteudo: 'Conteúdo',
  pautas: 'Pautas',
  artigos: 'Artigos',
  conectores: 'Conectores',
  calendario: 'Calendário',
  otimizacao: 'Otimização',
  configuracoes: 'Configurações',
  leads: 'Leads',
  funil: 'Funil',
  relatorios: 'Relatórios',
  aprovacoes: 'Aprovações',
  integracoes: 'Integrações',
  usuarios: 'Usuários',
  organizacao: 'Organização',
  prompts: 'Prompts de IA',
  custos: 'Custos de IA',
  auditoria: 'Auditoria',
  'alterar-senha': 'Alterar senha',
};
